import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { OP_LABELS, shapeOf, valueInputType } from './lib/filters';

const SELECT = 'rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm';
const INPUT = 'rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm';

/** Group all filterable fields by their breadcrumb prefix for an <optgroup> list. */
function groupedFieldOptions(index) {
  const groups = new Map();
  for (const entry of index.fieldIndex.values()) {
    if (entry.filterable === false) continue;
    const key = entry.labels.length ? entry.labels.join(' › ') : 'This row';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  }
  return [...groups.entries()].sort((a, b) => (a[0] === 'This row' ? -1 : b[0] === 'This row' ? 1 : a[0].localeCompare(b[0])));
}

function ChipsInput({ values, onChange, inputType }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-300 bg-white px-2 py-1 min-h-[38px]">
      {values.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          {v}
          <button type="button" className="text-gray-400 hover:text-gray-700" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type={inputType}
        className="flex-1 min-w-[6rem] border-0 p-0 text-sm focus:ring-0"
        placeholder={values.length ? '' : 'Type a value and press Enter'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && !draft && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}

function ValueInput({ filter, entry, onChange }) {
  const shape = shapeOf(filter.op);
  if (shape === 'none') return null;
  const type = entry?.type || 'string';
  const inputType = valueInputType(type);

  if (shape === 'pair') {
    const [lo, hi] = Array.isArray(filter.value) ? filter.value : ['', ''];
    return (
      <div className="flex items-center gap-2">
        <input type={inputType} className={INPUT} value={lo ?? ''} onChange={(e) => onChange([e.target.value, hi])} placeholder="from" />
        <span className="text-gray-400 text-sm">and</span>
        <input type={inputType} className={INPUT} value={hi ?? ''} onChange={(e) => onChange([lo, e.target.value])} placeholder="to" />
      </div>
    );
  }

  if (shape === 'list') {
    const values = Array.isArray(filter.value) ? filter.value : [];
    if (type === 'enum' && entry?.choices?.length) {
      return (
        <div className="flex flex-wrap gap-3 rounded-lg border border-gray-300 px-3 py-2 max-h-40 overflow-auto">
          {entry.choices.map((c) => (
            <label key={c.value} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={values.includes(c.value)}
                onChange={(e) => onChange(e.target.checked ? [...values, c.value] : values.filter((v) => v !== c.value))}
              />
              {c.label}
            </label>
          ))}
        </div>
      );
    }
    return <ChipsInput values={values.map(String)} onChange={onChange} inputType={inputType} />;
  }

  if (type === 'boolean') {
    return (
      <select className={SELECT} value={filter.value === true ? 'true' : filter.value === false ? 'false' : ''} onChange={(e) => onChange(e.target.value === 'true')}>
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (type === 'enum' && entry?.choices?.length) {
    return (
      <select className={SELECT} value={filter.value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {entry.choices.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
    );
  }
  return <input type={inputType} className={INPUT} value={filter.value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="value" />;
}

/**
 * One filter: field (grouped by relation) → operator (per type) → value input shaped by the operator.
 */
const FilterRow = ({ filter, index, catalogIndex, opsByType, errors = [], onChange, onRemove }) => {
  const entry = filter.field ? catalogIndex.fieldIndex.get(filter.field) : null;
  const missing = !!filter.field && !entry;
  const ops = entry ? opsByType[entry.type] || [] : [];
  const groups = groupedFieldOptions(catalogIndex);

  return (
    <div className="rounded-lg border border-gray-200 p-3 bg-white">
      <div className="grid gap-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,2fr)_auto] items-start">
        <select
          className={SELECT}
          value={filter.field || ''}
          onChange={(e) => {
            const next = catalogIndex.fieldIndex.get(e.target.value);
            onChange({ field: e.target.value }, next?.type);
          }}
          aria-label={`Filter ${index + 1} field`}
        >
          <option value="">Choose a field…</option>
          {missing && <option value={filter.field}>⚠ {filter.field} (missing)</option>}
          {groups.map(([group, entries]) => (
            <optgroup key={group} label={group}>
              {entries.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                  {f.isRollup ? ' (rollup)' : f.filterOnly ? ' (filter only)' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <select className={SELECT} value={filter.op} disabled={!entry} onChange={(e) => onChange({ op: e.target.value })} aria-label={`Filter ${index + 1} operator`}>
          {!entry && <option value={filter.op}>{OP_LABELS[filter.op] || filter.op}</option>}
          {ops.map((op) => (
            <option key={op} value={op}>
              {OP_LABELS[op] || op}
            </option>
          ))}
        </select>
        <div>{entry ? <ValueInput filter={filter} entry={entry} onChange={(value) => onChange({ value })} /> : null}</div>
        <button type="button" onClick={onRemove} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Remove filter">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {entry && (entry.filterOnly || entry.isRollup) && (
        <p className="mt-1 text-xs text-gray-500">
          {entry.isRollup
            ? 'Filters rows by the rolled-up value for the selected date range.'
            : 'Applied inside the related records: rows without a matching related record are dropped.'}
        </p>
      )}
      {errors.map((e, i) => (
        <p key={i} className="mt-1 text-sm text-red-600">
          {e}
        </p>
      ))}
    </div>
  );
};

export default FilterRow;
